import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

export const EASE = {
  out: "power3.out",
  soft: "power2.out",
  in: "power3.in",
  inOut: "power3.inOut",
  elastic: "elastic.out(1, 0.4)",
  expo: "expo.out",
} as const;

// ─── Text helpers ────────────────────────────────────────────────────────────

export function splitLinesIntoSpans(
  container: HTMLElement,
  lines: string[]
): HTMLElement[] {
  container.innerHTML = "";
  lines.forEach((line) => {
    const lineEl = document.createElement("span");
    lineEl.className = "block overflow-hidden";
    const inner = document.createElement("span");
    inner.textContent = line;
    inner.className = "inline-block will-change-transform";
    lineEl.appendChild(inner);
    container.appendChild(lineEl);
  });
  return Array.from(
    container.querySelectorAll("span > span")
  ) as HTMLElement[];
}

export function revealTextTimeline(opts: {
  locationEl: HTMLElement | null;
  titleSpans: HTMLElement[];
  descSpan: HTMLElement | null;
  reduced: boolean;
}): gsap.core.Timeline {
  const { locationEl, titleSpans, descSpan, reduced } = opts;
  const tl = gsap.timeline();

  if (reduced) {
    if (locationEl) gsap.set(locationEl, { opacity: 1, y: 0 });
    gsap.set(titleSpans, { y: 0, opacity: 1, filter: "blur(0px)" });
    if (descSpan) gsap.set(descSpan, { y: 0, opacity: 1 });
    return tl;
  }

  if (locationEl) gsap.set(locationEl, { opacity: 0, y: 8 });
  gsap.set(titleSpans, { y: "110%", opacity: 0, filter: "blur(8px)" });
  if (descSpan) gsap.set(descSpan, { y: 16, opacity: 0 });

  if (locationEl) {
    tl.to(locationEl, { opacity: 1, y: 0, duration: 0.5, ease: EASE.soft });
  }
  tl.to(
    titleSpans,
    {
      y: "0%",
      opacity: 1,
      filter: "blur(0px)",
      duration: 0.85,
      stagger: 0.1,
      ease: EASE.out,
    },
    locationEl ? "-=0.3" : 0
  );
  if (descSpan) {
    tl.to(
      descSpan,
      { y: 0, opacity: 1, duration: 0.6, ease: EASE.soft },
      "-=0.5"
    );
  }
  return tl;
}

// ─── Background crossfade ─────────────────────────────────────────────────────
// Called ONLY on slide change (not on first render).
// The initial state is set via inline styles + a one-time gsap.set in the component.

export function crossfadeBackground(opts: {
  incoming: HTMLElement;
  outgoing: HTMLElement | null;
  duration: number; // autoplay ms — used for Ken Burns duration
  reduced: boolean;
}): void {
  const { incoming, outgoing, duration, reduced } = opts;

  // Kill any in-progress tweens on these elements to prevent conflicts
  gsap.killTweensOf(incoming);
  if (outgoing) gsap.killTweensOf(outgoing);

  if (reduced) {
    gsap.set(incoming, { opacity: 1, scale: 1 });
    if (outgoing) gsap.set(outgoing, { opacity: 0, scale: 1 });
    return;
  }

  // ── Outgoing: fade out quickly ──────────────────────────────────────────
  if (outgoing) {
    gsap.to(outgoing, {
      opacity: 0,
      duration: 0.8,
      ease: EASE.soft,
    });
  }

  // ── Incoming: start from current opacity (already 0 from init),
  //    fade in smoothly + Ken Burns scale ──────────────────────────────────
  // We do NOT use fromTo here — the element is already at opacity:0
  // from the initial gsap.set, so just animate TO the end state.
  gsap.to(incoming, {
    opacity: 1,
    duration: 0.9,
    ease: EASE.soft,
  });

  // Ken Burns — separate tween so it doesn't conflict with opacity
  gsap.fromTo(
    incoming,
    { scale: 1.06 },
    {
      scale: 1,
      duration: duration / 1000 + 2,
      ease: "none",
    }
  );
}

// ─── Magnetic button ──────────────────────────────────────────────────────────

export function magneticMove(
  el: HTMLElement,
  e: React.MouseEvent<HTMLElement>,
  strength = 0.35
): void {
  const r = el.getBoundingClientRect();
  const x = (e.clientX - r.left - r.width / 2) * strength;
  const y = (e.clientY - r.top - r.height / 2) * strength;
  gsap.to(el, { x, y, duration: 0.3, ease: EASE.soft, overwrite: "auto" });
}

export function magneticReset(el: HTMLElement): void {
  gsap.to(el, {
    x: 0,
    y: 0,
    duration: 0.5,
    ease: EASE.elastic,
    overwrite: "auto",
  });
}